import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useApi, ApiError } from '../lib/api';
import { useMe } from '../lib/me';
import { money, periodLabel } from '../lib/format';
import PeriodPicker, { usePeriod } from '../components/PeriodPicker';

interface Def {
  key: string;
  label: string;
  clause: string;
  capped: boolean;
  internalBucket: string;
  source: { kind: 'gl' | 'manual' | 'vault_launch'; accounts?: string[] };
  entities: ('allstar' | 'rowcal')[];
}
interface Definitions {
  adjustments: Def[];
  bucketLabels: Record<string, string>;
  capPct: number;
  vault: { inputs: string[]; marketingRate: number; fixedRate: number };
}
interface Line { key: string; label: string; clause: string; capped: boolean; internalBucket: string; allstar: number; rowcal: number; total: number; basis: string }
interface Preview {
  ladder: Record<string, number>;
  adjustments: Line[];
  vault: Record<string, number | null>;
  warnings: string[];
}
interface Inputs { manual: { adjustment_key: string; entity: string; amount: number }[] }

const VAULT_LABELS: Record<string, string> = {
  vault_revenue: 'Vault revenue',
  vault_marketing_spend: 'Vault marketing spend (actual)',
  vault_fixed_costs: 'Vault start-up fixed costs (actual)',
};

const clauseLabel = (c: string) => `(${c.replace(/^([A-Z])(\d)$/, '$1)($2')})`;

export default function Adjustments() {
  const api = useApi();
  const qc = useQueryClient();
  const isAdmin = useMe().data?.role === 'admin';
  const [period, setPeriod] = usePeriod();
  const [draft, setDraft] = useState<Record<string, string>>({});

  const defs = useQuery({ queryKey: ['defs'], queryFn: () => api.get<Definitions>('/api/adjustments/definitions') });
  const inputs = useQuery({ queryKey: ['inputs', period], queryFn: () => api.get<Inputs>(`/api/adjustments/inputs?period=${period}`) });
  const preview = useQuery({
    queryKey: ['preview', period],
    queryFn: () => api.get<Preview>(`/api/adjustments/preview?period=${period}`),
    retry: false,
  });

  useEffect(() => {
    const d: Record<string, string> = {};
    for (const m of inputs.data?.manual ?? []) d[`${m.adjustment_key}|${m.entity}`] = String(m.amount);
    setDraft(d);
  }, [inputs.data]);

  const save = useMutation({
    mutationFn: () => api.put('/api/adjustments/inputs', Object.entries(draft)
      .filter(([, v]) => v.trim() !== '' && Number.isFinite(Number(v)))
      .map(([k, v]) => {
        const [adjustment_key, entity] = k.split('|');
        return { period, adjustment_key, entity, amount: Number(v) };
      })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inputs', period] });
      qc.invalidateQueries({ queryKey: ['preview', period] });
    },
  });

  const manualDefs = defs.data?.adjustments.filter((d) => d.source.kind === 'manual') ?? [];
  const field = (key: string, entity: 'allstar' | 'rowcal') => (
    <input className="border border-allstar-gray rounded px-2 py-1 w-36 text-right tabular-nums" disabled={!isAdmin}
      value={draft[`${key}|${entity}`] ?? ''} placeholder="0"
      onChange={(e) => setDraft({ ...draft, [`${key}|${entity}`]: e.target.value })} />
  );

  const notReady = preview.error instanceof ApiError && preview.error.status === 409 ? preview.error : null;
  const p = preview.data;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl text-denim">Addbacks & Synergies — {periodLabel(period)}</h1>
          <p className="text-sm text-allstar-gray-dark mt-1">
            One definition per item. The internal view and the MidCap package both read these exact amounts.
          </p>
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="card p-5">
          <h2 className="font-semibold text-denim mb-3">Synergies (manual, monthly)</h2>
          <table className="w-full text-sm">
            <tbody>
              {manualDefs.map((d) => (
                <tr key={d.key}>
                  <td className="py-1.5 pr-3">{d.label} <span className="text-xs text-allstar-gray-dark">{clauseLabel(d.clause)}</span></td>
                  <td className="py-1.5 text-right">{field(d.key, 'allstar')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card p-5">
          <h2 className="font-semibold text-denim mb-1">Vault launch & ramp inputs</h2>
          <p className="text-xs text-allstar-gray-dark mb-3">
            Addback = marketing above {((defs.data?.vault.marketingRate ?? 0.16) * 100).toFixed(0)}% of Vault revenue + start-up fixed costs
            above {((defs.data?.vault.fixedRate ?? 0.27) * 100).toFixed(0)}%. Replaces Vault-brand 90005 entries from Hub report 180.
          </p>
          <table className="w-full text-sm">
            <tbody>
              {(defs.data?.vault.inputs ?? []).map((k) => (
                <tr key={k}><td className="py-1.5 pr-3">{VAULT_LABELS[k] ?? k}</td><td className="py-1.5 text-right">{field(k, 'allstar')}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-3">
          <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Save inputs</button>
          {save.error && <span className="text-negative text-sm">{(save.error as Error).message}</span>}
        </div>
      )}

      {notReady && (
        <div className="card p-4 border-l-4 border-l-caution text-sm">
          <div className="font-semibold">Can't calculate this month yet</div>
          <div>{notReady.message}</div>
        </div>
      )}
      {preview.error && !notReady && <div className="text-negative text-sm">{(preview.error as Error).message}</div>}

      {p && (
        <>
          {p.warnings.map((w) => (
            <div key={w} className="card p-3 border-l-4 border-l-caution text-sm flex gap-2"><AlertTriangle className="w-4 h-4 text-caution shrink-0 mt-0.5" />{w}</div>
          ))}
          <div className="card overflow-hidden">
            <table className="table-default">
              <thead>
                <tr><th>Item</th><th>Internal line</th><th>Clause</th><th>Capped</th><th>Basis</th><th className="num">Allstar</th><th className="num">Rowcal</th><th className="num">Total</th></tr>
              </thead>
              <tbody>
                {p.adjustments.map((a) => (
                  <tr key={a.key}>
                    <td>{a.label}</td>
                    <td>{defs.data?.bucketLabels[a.internalBucket]}</td>
                    <td>{clauseLabel(a.clause)}</td>
                    <td>{a.capped ? '30%' : ''}</td>
                    <td className="text-xs">{a.basis}</td>
                    <td className="num">{money(a.allstar)}</td>
                    <td className="num">{money(a.rowcal)}</td>
                    <td className="num font-semibold">{money(a.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card p-5 max-w-md">
            <h2 className="font-semibold text-denim mb-2">EBITDA ladder (consolidated)</h2>
            <table className="w-full text-sm">
              <tbody>
                {[
                  ['EBITDA', 'ebitda', true], ['EBITDA Adjustments', 'ebitdaAdjustments', false], ['Adjusted EBITDA', 'adjustedEbitda', true],
                  ['Discontinued / Startup Ops', 'discontinuedStartup', false], ['Synergies', 'synergies', false], ['Bank EBITDA', 'bankEbitda', true],
                ].map(([label, k, bold]) => (
                  <tr key={k as string} className={bold ? 'font-semibold border-t border-allstar-gray' : ''}>
                    <td className="py-1">{label}</td><td className="py-1 text-right tabular-nums">{money(p.ladder[k as string])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
